import type {
  FeatureCatalogue,
  FeatureSpec,
  FeatureTable,
  FeatureValue,
} from '@/features/billing/api/billing';
import { DEFAULT_I18N } from '@/lib/i18n/context';
import { formatNumber } from '@/lib/i18n/format';
import type { Translator } from '@/lib/i18n/translate';

/**
 * Reading a plan's feature table.
 *
 * Nothing here knows which features exist or what any plan grants - that all
 * arrives from the API. These are the few rules for turning a stored value
 * into words, and for saying what one plan has that another does not, shared
 * by the platform panel and the merchant billing page so the two never
 * disagree on what a cell means.
 *
 * The words they produce come from the `billing` dictionary. Each takes an
 * optional `t`, English by default, so a caller outside a component (or a test)
 * gets the same text it always did and a component passes its own `useT()`.
 */

/** The smallest shape both screens can hand to the comparison table. */
export interface PlanColumn {
  code: string;
  name: string;
  price_minor: number;
  currency: string;
  billing_interval: string;
  /** Razorpay's `interval`: charge every N of the above. Absent means 1. */
  billing_interval_count?: number;
  features: FeatureTable;
}

export interface CategoryGroup {
  key: string;
  label: string;
  features: FeatureSpec[];
}

/** True when a stored value means "you may use this at all". */
export function isGranted(value: FeatureValue | undefined): boolean {
  if (value === true || value === null || value === 'ADDON') return true;
  if (typeof value === 'number') return value > 0;
  return false;
}

export function formatLimit(value: number | null, t: Translator = DEFAULT_I18N.t): string {
  return value === null ? t('billing.limit.unlimited') : formatNumber(value);
}

/**
 * A stored quota as the number a person types and reads.
 *
 * Most quotas store and show the same unit, so this is the identity. Setup
 * assistance stores minutes and shows hours, and doing the division here - in
 * the one place both panels and the pricing page read - is what stops the three
 * of them disagreeing about whether 450 means seven and a half hours.
 */
export function toDisplayValue(spec: FeatureSpec, stored: number): number {
  const scale = spec.display_scale || 1;
  if (scale === 1) return stored;
  return Math.round((stored / scale) * 100) / 100;
}

/** The inverse: a figure typed in the display unit, as the unit we store. */
export function toStoredValue(spec: FeatureSpec, shown: number): number {
  const scale = spec.display_scale || 1;
  return scale === 1 ? Math.round(shown) : Math.round(shown * scale);
}

/** What a quota counts, as it should be labelled: "products", "hours". */
export function displayUnit(spec: FeatureSpec): string | null {
  return spec.display_unit ?? spec.unit;
}

/** A quota rendered for reading: "20 hours", "2,000". */
export function formatQuota(
  spec: FeatureSpec,
  stored: number | null,
  t: Translator = DEFAULT_I18N.t,
): string {
  if (stored === null) return t('billing.limit.unlimited');
  const shown = toDisplayValue(spec, stored);
  // The unit is the server's word for what the quota counts, shown as sent.
  const unit = spec.display_unit;
  return unit
    ? `${formatLimit(shown, t)} ${shown === 1 ? unit.replace(/s$/, '') : unit}`
    : formatLimit(shown, t);
}

export type CellKind = 'yes' | 'no' | 'addon' | 'limit';

export interface CellPresentation {
  kind: CellKind;
  /** Short text: a number, "Unlimited", "Included", "Not included", "Add-on". */
  text: string;
}

/** How to show one value in a comparison cell. */
export function presentValue(
  spec: FeatureSpec,
  value: FeatureValue | undefined,
  t: Translator = DEFAULT_I18N.t,
): CellPresentation {
  const notIncluded: CellPresentation = { kind: 'no', text: t('billing.value.not_included') };
  if (spec.kind === 'QUOTA') {
    if (value === null || value === undefined) {
      return { kind: 'limit', text: t('billing.limit.unlimited') };
    }
    if (typeof value === 'number') {
      return value > 0 ? { kind: 'limit', text: formatQuota(spec, value, t) } : notIncluded;
    }
    return notIncluded;
  }
  if (value === 'ADDON') return { kind: 'addon', text: t('billing.value.addon') };
  return value === true ? { kind: 'yes', text: t('billing.value.included') } : notIncluded;
}

/** Features grouped under their category, in catalogue order. */
export function groupByCategory(catalogue: FeatureCatalogue): CategoryGroup[] {
  const byKey = new Map<string, CategoryGroup>(
    catalogue.categories.map((category) => [
      category.key,
      { key: category.key, label: category.label, features: [] },
    ]),
  );
  for (const feature of catalogue.features) {
    const group = byKey.get(feature.category);
    if (group) group.features.push(feature);
  }
  return [...byKey.values()].filter((group) => group.features.length > 0);
}

/** The key a comparison-table cell is tracked under while it has unsaved edits. */
export function cellKey(planCode: string, featureKey: string): string {
  return `${planCode}:${featureKey}`;
}

/** Whether every plan answers the same for this feature. */
export function isUniform(key: string, plans: PlanColumn[]): boolean {
  if (plans.length < 2) return true;
  const first = JSON.stringify(plans[0].features[key] ?? null);
  return plans.every((plan) => JSON.stringify(plan.features[key] ?? null) === first);
}

export interface LimitChange {
  spec: FeatureSpec;
  from: number | null;
  to: number | null;
}

/** What `to` has that `from` does not, and vice versa. */
export interface PlanDelta {
  /** Switches off on `from` and on (or add-on) on `to`. */
  gained: FeatureSpec[];
  /** Switches on on `from` and off on `to`. */
  lost: FeatureSpec[];
  raised: LimitChange[];
  lowered: LimitChange[];
}

function asLimit(value: FeatureValue | undefined): number | null {
  return typeof value === 'number' ? value : null;
}

export function planDelta(
  features: FeatureSpec[],
  from: FeatureTable,
  to: FeatureTable,
): PlanDelta {
  const delta: PlanDelta = { gained: [], lost: [], raised: [], lowered: [] };
  for (const spec of features) {
    const before = from[spec.key];
    const after = to[spec.key];
    if (spec.kind === 'QUOTA') {
      const low = asLimit(before);
      const high = asLimit(after);
      if (low === high) continue;
      // null is unlimited, which beats any number.
      const rose = high === null || (low !== null && high > low);
      (rose ? delta.raised : delta.lowered).push({ spec, from: low, to: high });
    } else {
      const had = isGranted(before);
      const has = isGranted(after);
      if (has && !had) delta.gained.push(spec);
      if (had && !has) delta.lost.push(spec);
    }
  }
  return delta;
}

/**
 * The cheapest plan that grants `key`. For a quota, pass `above` - the limit
 * the merchant is on - so the answer is a plan with *more*, not merely one
 * that has the feature (which is usually the plan they are already on).
 */
export function cheapestPlanWith(
  plans: PlanColumn[],
  key: string,
  above?: number | null,
): PlanColumn | undefined {
  // Already unlimited: no plan can offer more.
  if (above === null) return undefined;
  // Only a paid plan is an upgrade (a ₹0 test plan is not something to sell),
  // and a monthly one beats a longer cycle, compared per month: the same rule
  // the API's `upgrade_to` follows.
  const months = (plan: PlanColumn) =>
    cycleMonths(plan.billing_interval, plan.billing_interval_count ?? 1);
  const sorted = plans
    .filter((plan) => plan.price_minor > 0)
    .sort(
      (a, b) =>
        Number(months(a) !== 1) - Number(months(b) !== 1) ||
        a.price_minor / months(a) - b.price_minor / months(b),
    );
  return sorted.find((plan) => {
    const value = plan.features[key];
    if (!isGranted(value)) return false;
    if (above === undefined || value === null) return true;
    return typeof value === 'number' && value > above;
  });
}

export function intervalLabel(
  interval: string,
  style: 'short' | 'long' = 'short',
  t: Translator = DEFAULT_I18N.t,
): string {
  const known = interval === 'YEARLY' || interval === 'QUARTERLY' ? interval : 'MONTHLY';
  return t(`billing.interval.${style}.${known}`);
}

/** Calendar months in one billing cycle: the interval times how many of them. */
export function cycleMonths(interval: string, count = 1): number {
  const perInterval = interval === 'YEARLY' ? 12 : interval === 'QUARTERLY' ? 3 : 1;
  return perInterval * Math.max(count, 1);
}

/**
 * What a cycle price works out at per calendar month, in minor units.
 *
 * The figure every screen that sells a plan leads with, because it is the only
 * one that compares across the cadences: a quarterly price beside a monthly
 * one reads as four times the product. It is never the whole story, so the
 * screens that show it also show the charge it came from.
 *
 * `null` when there is nothing to convert - a plan that already bills monthly
 * is its own per-month price, and "₹999 a month, ₹999 billed monthly" is one
 * fact said twice.
 *
 * Rounded to whole rupees, here rather than in each caller, because nobody is
 * ever charged this number: it is an equivalent, and the two screens that
 * print it - one in whole rupees, one to the paisa - have to print the same
 * one. The paise the owner set are what gets charged, as the cycle price.
 */
export function perMonthMinor(price_minor: number, interval: string, count = 1): number | null {
  const months = cycleMonths(interval, count);
  if (months <= 1) return null;
  return Math.round(price_minor / months / 100) * 100;
}

/** How many features a plan card lists before it starts to read as a wall. */
export const CARD_HIGHLIGHTS = 8;

/**
 * The handful of features a plan's own card leads with.
 *
 * The plans table carries a `feature_order` the platform owner sets per plan,
 * and where it is set it is the whole answer: those features, in that order,
 * are what this plan is sold on. Where it is not - which is every plan until
 * somebody arranges one - the card falls back to the quotas, because the
 * numbers are what separate one plan from the next at a glance.
 *
 * Shared by the pricing page and the subscribe chooser so that a merchant who
 * picked a plan off the marketing page is shown the same plan when they are
 * asked to pay for it. A card arranged in the panel and a card that ignores
 * the arrangement are two different offers.
 */
export function cardHighlights(
  plan: { features: FeatureTable; feature_order?: Record<string, number> },
  features: FeatureSpec[],
  quotas: FeatureSpec[],
): FeatureSpec[] {
  const placed = plan.feature_order ?? {};
  const keys = Object.keys(placed);
  if (keys.length === 0) return quotas;

  const byKey = new Map(features.map((spec) => [spec.key, spec]));
  return keys
    .filter((key) => byKey.has(key) && isGranted(plan.features[key]))
    .sort((a, b) => placed[a] - placed[b])
    .map((key) => byKey.get(key) as FeatureSpec)
    .slice(0, CARD_HIGHLIGHTS);
}

/**
 * What follows the price: "month", "3 months", "year" - generated from the
 * plan's configured cycle, never assumed. `short` gives the compact "mo" /
 * "yr" for tables, falling back to the long form where no abbreviation reads.
 */
export function billingCycleLabel(
  interval: string,
  count = 1,
  style: 'short' | 'long' = 'long',
  t: Translator = DEFAULT_I18N.t,
): string {
  const months = cycleMonths(interval, count);
  if (months === 1) return t(`billing.interval.${style}.MONTHLY`);
  if (months === 12) return t(`billing.interval.${style}.YEARLY`);
  if (months % 12 === 0) return t('billing.cycle.years', { years: months / 12 });
  return t('billing.cycle.months', { months });
}

/** "monthly" / "quarterly" / "yearly" / "every 6 months" - for "Billed …". */
export function billingCycleAdverb(interval: string, count = 1, t: Translator = DEFAULT_I18N.t): string {
  const months = cycleMonths(interval, count);
  if (months === 1) return t('billing.cycle.adverb.monthly');
  if (months === 3) return t('billing.cycle.adverb.quarterly');
  if (months === 12) return t('billing.cycle.adverb.yearly');
  return t('billing.cycle.adverb.every', { cycle: billingCycleLabel(interval, count, 'long', t) });
}

/**
 * The billing cadences a set of plans is sold on, shortest cycle first.
 *
 * Ordered by cycle length rather than by the plans' own sort order, so the
 * cadence switch reads monthly, quarterly, yearly however the catalogue
 * happens to be sorted.
 */
export function billingIntervalsOf(plans: { billing_interval: string }[]): string[] {
  return [...new Set(plans.map((plan) => plan.billing_interval))].sort(
    (a, b) => cycleMonths(a) - cycleMonths(b),
  );
}
